import { inject, Injectable } from '@angular/core';

import { environment } from '@qwyk/portals/environment';

import { IconService } from './icon.service';
import { MarkerIconType } from '../constants';

declare let google: any;
declare let mapboxgl: any;

@Injectable({
    providedIn: 'root',
})
export class MapService {
    private readonly iconService = inject(IconService);

    private provider: 'google' | 'mapbox' | null = null;
    private mapInstance: any = null;
    private containerId: string | null = null;
    private infoWindow: any = null;
    private googleMapsPromise: Promise<void> | null = null;
    private googleApi: any = null;

    private mapboxLayers: Set<string> = new Set();
    private mapboxMarkers: any[] = [];
    private googleOverlays: any[] = [];

    constructor() {
        this.injectGlobalStyles();
    }

    addMarker(
        lng: number,
        lat: number,
        title?: string,
        tooltipText?: string,
        color = '#EA4335',
        iconType: MarkerIconType = 'pin'
    ) {
        if (!this.mapInstance || !this.provider) return;

        const plainTitle = title ? this.stripHtml(title) : undefined;

        if (this.provider === 'google') {
            return this.addGoogleMarker(
                lng,
                lat,
                plainTitle,
                tooltipText,
                color,
                iconType
            );
        } else if (this.provider === 'mapbox') {
            return this.addMapboxMarker(
                lng,
                lat,
                plainTitle,
                tooltipText,
                color,
                iconType
            );
        }
    }

    addPolyline(path: { lat: number; lng: number }[], options?: any) {
        if (!this.mapInstance || !this.provider) return;

        if (this.provider === 'google') {
            const finalOptions = { ...options };

            if (options?.iconStyle === 'FORWARD_OPEN_ARROW') {
                const arrow = {
                    path: this.googleApi.maps.SymbolPath.FORWARD_OPEN_ARROW,
                    fillColor: 'gray',
                    fillOpacity: 1,
                    strokeColor: 'gray',
                    strokeWeight: 0.4,
                };
                finalOptions.icons = [
                    {
                        icon: arrow,
                        repeat: options.iconRepeat || '70px',
                    },
                ];
                delete finalOptions.iconStyle;
                delete finalOptions.iconRepeat;
            }

            return new this.googleApi.maps.Polyline({
                ...finalOptions,
                path,
                map: this.mapInstance,
            });
        } else if (this.provider === 'mapbox') {
            let pathPoints = path;
            if (options?.geodesic) {
                pathPoints = this.densifyGeodesicPath(path);
            }
            const coordinates = this.ensureContinuousPath(pathPoints);
            const layerId = `route-${Date.now()}`;
            const arrowLayerId = `${layerId}-arrows`;

            if (!this.mapInstance.hasImage('arrow')) {
                const arrowIcon = this.createArrowIcon();
                this.mapInstance.addImage('arrow', arrowIcon, { sdf: true });
            }

            this.mapInstance.addSource(layerId, {
                type: 'geojson',
                data: {
                    type: 'Feature',
                    properties: {},
                    geometry: {
                        type: 'LineString',
                        coordinates,
                    },
                },
            });

            this.mapInstance.addLayer({
                id: layerId,
                type: 'line',
                source: layerId,
                layout: {
                    'line-join': 'round',
                    'line-cap': 'round',
                },
                paint: {
                    'line-color': options?.strokeColor || '#888',
                    'line-width': options?.strokeWeight || 8,
                },
            });

            this.mapInstance.addLayer({
                id: arrowLayerId,
                type: 'symbol',
                source: layerId,
                layout: {
                    'symbol-placement': 'line',
                    'symbol-spacing': 70,
                    'icon-image': 'arrow',
                    'icon-size': 0.5,
                    'icon-allow-overlap': true,
                    'icon-ignore-placement': true,
                },
                paint: {
                    'icon-color': 'gray',
                },
            });

            this.mapboxLayers.add(layerId);
            this.mapboxLayers.add(arrowLayerId);

            return layerId;
        }
    }

    removeOverlay(overlay: any) {
        if (!this.mapInstance || !this.provider || !overlay) return;

        if (this.provider === 'google') {
            if (overlay instanceof this.HTMLMapMarker) {
                overlay.setMap(null);
                const index = this.googleOverlays.indexOf(overlay);
                if (index > -1) {
                    this.googleOverlays.splice(index, 1);
                }
            } else {
                overlay.setMap(null);
            }
        } else if (this.provider === 'mapbox') {
            if (typeof overlay === 'string') {
                const arrowLayerId = `${overlay}-arrows`;

                if (this.mapInstance.getLayer(arrowLayerId)) {
                    this.mapInstance.removeLayer(arrowLayerId);
                    this.mapboxLayers.delete(arrowLayerId);
                }

                if (this.mapInstance.getLayer(overlay)) {
                    this.mapInstance.removeLayer(overlay);
                    this.mapboxLayers.delete(overlay);
                }

                if (this.mapInstance.getSource(overlay)) {
                    this.mapInstance.removeSource(overlay);
                }
            } else if (overlay.remove) {
                overlay.remove();
                const index = this.mapboxMarkers.indexOf(overlay);
                if (index > -1) {
                    this.mapboxMarkers.splice(index, 1);
                }
            }
        }
    }

    destroyMap() {
        if (this.provider === 'mapbox') {
            this.clearMapboxContent();

            this.containerId = null;
            return;
        }

        if (this.mapInstance && this.provider === 'google') {
            this.containerId = null;
            return;
        }

        this.provider = null;
        this.containerId = null;
    }

    fitBounds(boundsPoints: { lat: number; lng: number }[]) {
        if (!this.mapInstance || !this.provider) return;

        if (this.provider === 'google') {
            const bounds = new this.googleApi.maps.LatLngBounds();
            boundsPoints.forEach(p =>
                bounds.extend(new this.googleApi.maps.LatLng(p.lat, p.lng))
            );
            this.mapInstance.fitBounds(bounds);
        } else if (this.provider === 'mapbox') {
            // Use ensureContinuousPath to normalize longitudes for Pacific crossing
            const normalizedPoints = this.ensureContinuousPath(boundsPoints);

            const bounds = new mapboxgl.LngLatBounds();
            normalizedPoints.forEach(p => bounds.extend([p[0], p[1]]));
            this.mapInstance.fitBounds(bounds, { padding: 50 });
        }
    }

    async initMap(containerId: string, options: any): Promise<void> {
        this.containerId = containerId;

        try {
            const scriptUrl = `https://maps.googleapis.com/maps/api/js?key=${environment.utils.maps.google.apiKey}&libraries=marker&loading=async`;

            const loadPromise = this.loadScript(scriptUrl, true);
            const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Google Maps timeout')), 5000)
            );

            await Promise.race([loadPromise, timeoutPromise]);

            this.provider = 'google';
            this.initGoogleMap(containerId, options);
        } catch (error) {
            this.provider = 'mapbox';
            await this.initMapbox(containerId, options);
        }
    }

    private addGoogleMarker(
        lng: number,
        lat: number,
        title: string | undefined,
        tooltipText: string | undefined,
        color: string,
        iconType: MarkerIconType
    ) {
        // Use Custom Overlay for Radar to support CSS animation
        if (iconType === 'radar') {
            const svgUrl = this.iconService.getSvgDataUrl(iconType, color);
            const overlay = new this.HTMLMapMarker(
                new this.googleApi.maps.LatLng(lat, lng),
                svgUrl,
                this.mapInstance,
                title,
                tooltipText,
                this.infoWindow
            );
            this.googleOverlays.push(overlay);
            return overlay;
        }

        // Get icon path and dimensions from FontAwesome
        const { path, viewBox } = this.iconService.getSvgPath(iconType);
        const { width, height, isPinStyle } =
            this.iconService.getIconDimensions(iconType);

        const marker = new this.googleApi.maps.Marker({
            position: { lat, lng },
            map: this.mapInstance,
            title: title,
            icon: {
                path: path,
                fillColor: color,
                fillOpacity: 1,
                strokeWeight: 1,
                strokeColor: '#ffffff',
                // Scale based on icon dimensions (target ~24-32px)
                scale: this.getIconScale(iconType, width, height),
                // Anchor at bottom center for pins, center for circular icons
                anchor: isPinStyle
                    ? new this.googleApi.maps.Point(width / 2, height)
                    : new this.googleApi.maps.Point(width / 2, height / 2),
                labelOrigin: new this.googleApi.maps.Point(
                    width / 2,
                    height * 0.4
                ),
            },
        });

        if (tooltipText) {
            marker.addListener('click', () => {
                this.infoWindow.setContent(tooltipText);
                this.infoWindow.open(this.mapInstance, marker);
            });
        }

        return marker;
    }

    private addMapboxMarker(
        lng: number,
        lat: number,
        title: string | undefined,
        tooltipText: string | undefined,
        color: string,
        iconType: MarkerIconType
    ) {
        const el = document.createElement('div');
        el.className = 'mapbox-custom-marker';

        // Add pulsating class for radar
        if (iconType === 'radar') {
            el.classList.add('pulsate-fwd');
        }

        const { isPinStyle } = this.iconService.getIconDimensions(iconType);

        const width = isPinStyle ? 27 : 32;
        const height = isPinStyle ? 43 : 32;
        const offset: [number, number] = isPinStyle ? [0, -21] : [0, 0];

        // Get SVG data URL from FontAwesome via IconService
        const svgUrl = this.iconService.getSvgDataUrl(iconType, color);
        el.style.backgroundImage = `url("${svgUrl}")`;
        el.style.width = `${width}px`;
        el.style.height = `${height}px`;
        el.style.backgroundSize = 'contain';
        el.style.backgroundRepeat = 'no-repeat';
        el.style.cursor = 'pointer';

        if (title) {
            el.title = title;
        }

        const marker = new mapboxgl.Marker({ element: el, offset: offset })
            .setLngLat([lng, lat])
            .addTo(this.mapInstance);

        this.mapboxMarkers.push(marker);

        if (tooltipText) {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = tooltipText;

            const popup = new mapboxgl.Popup({ offset: 25 }).setDOMContent(
                tempDiv
            );

            marker.setPopup(popup);
        }

        return marker;
    }

    private applyMapboxTheme(colors: { primary: string; light: string }) {
        if (!this.mapInstance || this.provider !== 'mapbox') return;

        try {
            const layers = this.mapInstance.getStyle().layers;
            if (!layers) return;

            layers.forEach(layer => {
                // Handle background layer
                if (layer.type === 'background') {
                    this.mapInstance.setPaintProperty(
                        layer.id,
                        'background-color',
                        colors.primary
                    );
                    return;
                }

                // Handle water layers
                if (layer.id.includes('water')) {
                    if (layer.type === 'fill') {
                        this.mapInstance.setPaintProperty(
                            layer.id,
                            'fill-color',
                            '#ffffff'
                        );
                    }
                    return;
                }

                // Handle other fill layers (likely land, landuse, buildings, etc.)
                // If it's not water, we generally want it to be the primary color (land)
                // or transparent if it's an overlay we don't want to emphasize.
                if (layer.type === 'fill') {
                    // Exclude specific layers if needed, but generally color them primary
                    this.mapInstance.setPaintProperty(
                        layer.id,
                        'fill-color',
                        colors.primary
                    );

                    // Some layers might have opacity set, ensure it's visible or adjusted as needed
                    // this.mapInstance.setPaintProperty(layer.id, 'fill-opacity', 1);
                }

                // Handle text color and halo for symbol layers
                if (layer.type === 'symbol') {
                    this.mapInstance.setPaintProperty(
                        layer.id,
                        'text-color',
                        '#ffffff'
                    );
                    this.mapInstance.setPaintProperty(
                        layer.id,
                        'text-halo-color',
                        colors.primary
                    );
                    this.mapInstance.setPaintProperty(
                        layer.id,
                        'text-halo-width',
                        1
                    ); // Ensure halo is visible
                }
            });
        } catch (e) {
            console.warn('Error applying Mapbox theme:', e);
        }
    }

    private clearMapboxContent() {
        if (!this.mapInstance || this.provider !== 'mapbox') return;

        this.mapboxMarkers.forEach(marker => {
            if (marker && marker.remove) {
                marker.remove();
            }
        });
        this.mapboxMarkers = [];

        const layersToRemove = Array.from(this.mapboxLayers);
        layersToRemove.forEach(id => {
            if (this.mapInstance.getLayer(id)) {
                this.mapInstance.removeLayer(id);
            }
        });

        layersToRemove.forEach(id => {
            if (this.mapInstance.getSource(id)) {
                this.mapInstance.removeSource(id);
            }
        });

        this.mapboxLayers.clear();
    }

    private createArrowIcon() {
        const width = 20;
        const height = 20;
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
            // Draw a chevron pointing right (>)
            ctx.strokeStyle = '#fff'; // White color for SDF base, will be tinted by icon-color
            ctx.lineWidth = 3;
            ctx.beginPath();
            ctx.moveTo(0, 0);
            ctx.lineTo(width, height / 2);
            ctx.lineTo(0, height);
            ctx.stroke();
        }
        return ctx.getImageData(0, 0, width, height);
    }

    private densifyGeodesicPath(
        path: { lat: number; lng: number }[]
    ): { lat: number; lng: number }[] {
        if (!path || path.length < 2) return path;

        const densifiedPath: { lat: number; lng: number }[] = [];

        for (let i = 0; i < path.length - 1; i++) {
            const p1 = path[i];
            const p2 = path[i + 1];

            densifiedPath.push(p1);

            const dist = this.distance(p1.lat, p1.lng, p2.lat, p2.lng);
            const steps = Math.ceil(dist / 50000);

            for (let j = 1; j < steps; j++) {
                const f = j / steps;
                const p = this.intermediatePoint(p1, p2, f);
                densifiedPath.push(p);
            }
        }
        densifiedPath.push(path[path.length - 1]);

        return densifiedPath;
    }

    private distance(
        lat1: number,
        lon1: number,
        lat2: number,
        lon2: number
    ): number {
        const R = 6371e3;
        const phi1 = (lat1 * Math.PI) / 180;
        const phi2 = (lat2 * Math.PI) / 180;
        const dPhi = ((lat2 - lat1) * Math.PI) / 180;
        const dLambda = ((lon2 - lon1) * Math.PI) / 180;

        const a =
            Math.sin(dPhi / 2) * Math.sin(dPhi / 2) +
            Math.cos(phi1) * Math.cos(phi2) * Math.sin(dLambda / 2) * Math.sin(dLambda / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

        return R * c;
    }

    private ensureContinuousPath(
        path: { lat: number; lng: number }[]
    ): number[][] {
        if (!path || path.length === 0) return [];

        const newPath: number[][] = [];
        let prevLng = path[0].lng;
        newPath.push([prevLng, path[0].lat]);

        for (let i = 1; i < path.length; i++) {
            let lng = path[i].lng;
            const lat = path[i].lat;

            let diff = lng - prevLng;

            while (diff > 180) {
                lng -= 360;
                diff -= 360;
            }
            while (diff < -180) {
                lng += 360;
                diff += 360;
            }

            newPath.push([lng, lat]);
            prevLng = lng;
        }

        return newPath;
    }

    private getIconScale(
        iconType: MarkerIconType,
        width: number,
        height: number
    ): number {
        const smallIcons: MarkerIconType[] = [
            'dot',
            'water',
            'cloud',
            'road',
            'train-track',
        ];
        if (smallIcons.includes(iconType)) {
            return 0.05;
        }

        return 0.06;
    }

    // Custom Overlay Class for Google Maps
    private get HTMLMapMarker() {
        return class extends google.maps.OverlayView {
            private latlng: any;
            private imageUrl: string;
            private div: HTMLElement | null = null;
            private title: string | undefined;
            private tooltipText: string | undefined;
            private infoWindow: any;
            private map: any;

            constructor(
                latlng: any,
                imageUrl: string,
                map: any,
                title?: string,
                tooltipText?: string,
                infoWindow?: any
            ) {
                super();
                this.latlng = latlng;
                this.imageUrl = imageUrl;
                this.map = map;
                this.title = title;
                this.tooltipText = tooltipText;
                this.infoWindow = infoWindow;
                this.setMap(map);
            }

            onAdd() {
                this.div = document.createElement('div');
                this.div.className = 'google-custom-marker pulsate-fwd';

                const pulseRing = document.createElement('div');
                pulseRing.className = 'pulse-ring';
                this.div.appendChild(pulseRing);

                const icon = document.createElement('img');
                icon.src = this.imageUrl;
                this.div.appendChild(icon);

                if (this.title) {
                    this.div.title = this.title;
                }

                if (this.tooltipText) {
                    this.div.addEventListener('click', e => {
                        e.stopPropagation(); // Prevent map click
                        if (this.infoWindow) {
                            this.infoWindow.setContent(this.tooltipText);
                            this.infoWindow.setPosition(this.latlng);
                            this.infoWindow.open(this.map);
                        }
                    });
                }

                const panes = this.getPanes();
                panes.overlayMouseTarget.appendChild(this.div);
            }

            draw() {
                if (!this.div) return;
                const overlayProjection = this.getProjection();
                const point = overlayProjection.fromLatLngToDivPixel(
                    this.latlng
                );
                if (point) {
                    this.div.style.left = point.x - 16 + 'px'; // Center horizontally (32/2)
                    this.div.style.top = point.y - 16 + 'px'; // Center vertically (32/2)
                }
            }

            onRemove() {
                if (this.div) {
                    this.div.parentNode?.removeChild(this.div);
                    this.div = null;
                }
            }
        };
    }

    private initGoogleMap(containerId: string, options: any) {
        const mapOptions = { ...options };

        if ('enableMapbox' in mapOptions) {
            delete mapOptions.enableMapbox;
        }
        if ('themeColors' in mapOptions) {
            delete mapOptions.themeColors;
        }

        const currentContainer = document.getElementById(containerId);
        if (
            this.mapInstance &&
            typeof this.mapInstance.getDiv === 'function' &&
            this.mapInstance.getDiv() === currentContainer
        ) {
            this.mapInstance.setOptions(mapOptions);
            if (options.center) {
                this.mapInstance.setCenter(options.center);
            }
            if (options.zoom) {
                this.mapInstance.setZoom(options.zoom);
            }
        } else {
            this.mapInstance = new this.googleApi.maps.Map(
                document.getElementById(containerId),
                mapOptions
            );
            this.infoWindow = new this.googleApi.maps.InfoWindow();
        }
    }

    private async initMapbox(containerId: string, options: any): Promise<void> {
        const enableMapbox = options.enableMapbox ?? true;

        if (!enableMapbox) {
            return;
        }

        await this.loadScript(
            'https://api.mapbox.com/mapbox-gl-js/v3.18.0/mapbox-gl.js'
        );
        await this.loadStylesheet(
            'https://api.mapbox.com/mapbox-gl-js/v3.18.0/mapbox-gl.css'
        );

        mapboxgl.accessToken = environment.utils.maps.mapbox.apiKey;

        return new Promise<void>(resolve => {
            const container = document.getElementById(containerId);

            if (
                this.mapInstance &&
                typeof this.mapInstance.getContainer === 'function'
            ) {
                this.clearMapboxContent();

                const currentContainer = this.mapInstance.getContainer();
                if (currentContainer && currentContainer.parentNode) {
                    currentContainer.parentNode.removeChild(currentContainer);
                }

                if (container) {
                    container.appendChild(this.mapInstance.getContainer());
                }

                this.mapInstance.setCenter([
                    options.center.lng,
                    options.center.lat,
                ]);
                this.mapInstance.setZoom(options.zoom || 12);

                this.mapInstance.resize();

                resolve();
            } else {
                this.mapInstance = new mapboxgl.Map({
                    container: containerId,
                    style: 'mapbox://styles/mapbox/light-v10', // Use light style as base for easier customization
                    center: [options.center.lng, options.center.lat],
                    zoom: options.zoom || 12,
                });

                this.mapInstance.on('load', () => {
                    if (options.themeColors) {
                        this.applyMapboxTheme(options.themeColors);
                    }
                    resolve();
                });
            }
        });
    }

    private injectGlobalStyles() {
        const styleId = 'qwyk-map-styles-v8'; // Bump version
        if (document.getElementById(styleId)) return;

        const style = document.createElement('style');
        style.id = styleId;
        style.innerHTML = `
            @keyframes pulse-green {
                0% {
                    transform: scale(0.5);
                    box-shadow: 0 0 0 0 rgba(40, 167, 69, 0.7);
                    opacity: 1;
                }
                70% {
                    transform: scale(2.5);
                    box-shadow: 0 0 0 20px rgba(40, 167, 69, 0);
                    opacity: 0;
                }
                100% {
                    transform: scale(0.5);
                    box-shadow: 0 0 0 0 rgba(40, 167, 69, 0);
                    opacity: 0;
                }
            }

            /* Mapbox Styles */
            .mapbox-custom-marker.pulsate-fwd {
                z-index: 10;
                overflow: visible !important;
            }

            .mapbox-custom-marker.pulsate-fwd::after {
                content: '';
                position: absolute;
                width: 100%;
                height: 100%;
                top: 0;
                left: 0;
                border-radius: 50%;
                background: rgba(40, 167, 69, 0.4);
                animation: pulse-green 2s infinite;
                z-index: -1;
                pointer-events: none;
            }

            /* Google Maps Styles */
            .google-custom-marker {
                position: absolute;
                cursor: pointer;
                z-index: 100;
                /* Ensure it has size */
                width: 32px;
                height: 32px;
            }

            .google-custom-marker .pulse-ring {
                position: absolute;
                width: 100%;
                height: 100%;
                top: 0;
                left: 0;
                border-radius: 50%;
                background: rgba(40, 167, 69, 0.4);
                animation: pulse-green 2s infinite;
                z-index: -1;
                pointer-events: none;
            }

            .google-custom-marker img {
                width: 100%;
                height: 100%;
                display: block;
                z-index: 1;
                position: relative;
            }
        `;
        document.head.appendChild(style);
    }

    private intermediatePoint(
        p1: { lat: number; lng: number },
        p2: { lat: number; lng: number },
        f: number
    ): { lat: number; lng: number } {
        const lat1 = (p1.lat * Math.PI) / 180;
        const lon1 = (p1.lng * Math.PI) / 180;
        const lat2 = (p2.lat * Math.PI) / 180;
        const lon2 = (p2.lng * Math.PI) / 180;

        const d =
            2 *
            Math.asin(
                Math.min(
                    1,
                    Math.sqrt(
                        Math.pow(Math.sin((lat1 - lat2) / 2), 2) +
                            Math.cos(lat1) *
                                Math.cos(lat2) *
                                Math.pow(Math.sin((lon1 - lon2) / 2), 2)
                    )
                )
            );

        if (d < 1e-6) {
            return {
                lat: p1.lat + f * (p2.lat - p1.lat),
                lng: p1.lng + f * (p2.lng - p1.lng),
            };
        }

        const A = Math.sin((1 - f) * d) / Math.sin(d);
        const B = Math.sin(f * d) / Math.sin(d);

        const x =
            A * Math.cos(lat1) * Math.cos(lon1) +
            B * Math.cos(lat2) * Math.cos(lon2);
        const y =
            A * Math.cos(lat1) * Math.sin(lon1) +
            B * Math.cos(lat2) * Math.sin(lon2);
        const z = A * Math.sin(lat1) + B * Math.sin(lat2);

        const lat = Math.atan2(z, Math.sqrt(Math.pow(x, 2) + Math.pow(y, 2)));
        const lon = Math.atan2(y, x);

        return {
            lat: (lat * 180) / Math.PI,
            lng: (lon * 180) / Math.PI,
        };
    }

    private loadScript(
        src: string,
        useCallbackForGoogle = false
    ): Promise<void> {
        if (useCallbackForGoogle) {
            if (this.googleApi) {
                return Promise.resolve();
            }
            if (!this.googleMapsPromise) {
                this.googleMapsPromise = new Promise((resolve, reject) => {
                    const callbackName = 'mapApiLoaded';
                    const scriptSrc = `${src}&callback=${callbackName}`;

                    (window as any)[callbackName] = () => {
                        this.googleApi = window.google;
                        resolve();
                        delete (window as any)[callbackName];
                    };

                    const script = document.createElement('script');
                    script.src = scriptSrc;
                    script.async = true;
                    script.defer = true;
                    script.onerror = err => {
                        this.googleMapsPromise = null;
                        delete (window as any)[callbackName];
                        reject(err);
                    };
                    document.body.appendChild(script);
                });
            }
            return this.googleMapsPromise;
        }

        return new Promise((resolve, reject) => {
            if (document.querySelector(`script[src="${src}"]`)) {
                resolve();
                return;
            }
            const script = document.createElement('script');
            script.src = src;
            script.async = true;
            script.defer = true;
            script.onload = () => resolve();
            script.onerror = err => reject(err);
            document.body.appendChild(script);
        });
    }

    private loadStylesheet(href: string): Promise<void> {
        return new Promise((resolve, reject) => {
            if (document.querySelector(`link[href="${href}"]`)) {
                resolve();
                return;
            }
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = href;
            link.onload = () => resolve();
            link.onerror = err => reject(err);
            document.head.appendChild(link);
        });
    }

    private stripHtml(html: string): string {
        if (!html) return '';

        // Replace block tags with a separator
        let text = html.replace(/<\/?(div|p|br|li|tr)[^>]*>/gi, ' | ');

        // Remove remaining tags
        const tmp = document.createElement('DIV');
        tmp.innerHTML = text;
        text = tmp.textContent || tmp.innerText || '';

        // Clean up extra separators and spaces
        return text
            .replace(/\s*\|\s*/g, ' | ')
            .replace(/^\|\s*|\s*\|$/g, '')
            .trim();
    }
}
